import { storyPlainText, storyPrefix } from './storyEdit';

/**
 * Linked text stories: helpers for the Story Map (the chain a selected text box belongs to, in reading order,
 * with a snippet and word count per box).
 */
export const isTextType = (o) => !!o && (o.type === 'textbox' || o.type === 'text');
export const isLinkedText = (o) => isTextType(o) && !!(o.properties?.linkPrevId || o.properties?.linkNextId);

/** Every page object plus the shared scratch area — the set a story can span (links cross pages, like Publisher). */
export const documentObjects = (pages = [], scratch = null) => {
  const out = [];
  pages.forEach((pg) => (pg.objects || []).forEach((o) => out.push(o)));
  (scratch?.objects || []).forEach((o) => out.push(o));
  return out;
};

/** Where an object lives: { pageIndex } (0-based), { scratch: true }, or null. */
export const locateObject = (id, pages = [], scratch = null) => {
  for (let i = 0; i < pages.length; i += 1) if ((pages[i].objects || []).some((o) => o.id === id)) return { pageIndex: i };
  if ((scratch?.objects || []).some((o) => o.id === id)) return { scratch: true };
  return null;
};

/** Human label for a story box's location ("Page 2", "Scratch area"). */
export const locationLabel = (loc, pages = []) => (!loc ? '' : loc.scratch ? 'Scratch area' : pages[loc.pageIndex]?.name || `Page ${loc.pageIndex + 1}`);

/** The first box of every linked story (a linked box with no predecessor, or whose predecessor no longer exists). */
export const storyHeads = (objects = []) => {
  const ids = new Set(objects.map((o) => o.id));
  return objects.filter((o) => isLinkedText(o) && (!o.properties.linkPrevId || !ids.has(o.properties.linkPrevId)));
};

/**
 * Story repair after boxes disappear (deleted objects, a deleted page): the chain closes over the gap — the box
 * before a removed one now flows into the box after it — and when the *first* box goes, the story text moves to the
 * next box so no words are lost (Publisher behaviour). Returns Map<id, newProperties> for the surviving boxes.
 */
export const removeFromStories = (objects = [], deletedIds) => {
  const gone = deletedIds instanceof Set ? deletedIds : new Set(deletedIds || []);
  const byId = new Map(objects.map((o) => [o.id, o]));
  const patches = new Map();
  const propsOf = (o) => patches.get(o.id) || { ...(o.properties || {}) };
  objects.forEach((o) => {
    if (!gone.has(o.id) || !isLinkedText(o)) return;
    // survivors on either side of the removed box (skipping neighbours that are removed too)
    let prev = byId.get(o.properties.linkPrevId);
    while (prev && gone.has(prev.id)) prev = byId.get(prev.properties?.linkPrevId);
    let next = byId.get(o.properties.linkNextId);
    while (next && gone.has(next.id)) next = byId.get(next.properties?.linkNextId);
    if (prev) { const p = propsOf(prev); p.linkNextId = next ? next.id : null; patches.set(prev.id, p); }
    if (next) {
      const n = propsOf(next);
      n.linkPrevId = prev ? prev.id : null;
      if (!prev) {
        // the head is gone: the story text (from the first removed box of this run) moves to the new head
        let head = o;
        while (head.properties?.linkPrevId && byId.has(head.properties.linkPrevId) && gone.has(head.properties.linkPrevId)) head = byId.get(head.properties.linkPrevId);
        if (!head.properties?.linkPrevId || !byId.has(head.properties.linkPrevId)) {
          const hp = head.properties || {};
          if (hp.html) { n.html = hp.html; n.text = storyPlainText(hp); } else { n.text = hp.text || ''; n.html = undefined; }
        }
      }
      patches.set(next.id, n);
    }
  });
  return patches;
};

/** Every box of the story `objectId` belongs to, head first. Cycles and dangling links are tolerated. */
export const storyChainOf = (objectId, objects = []) => {
  const byId = new Map(objects.map((o) => [o.id, o]));
  let cur = byId.get(objectId);
  if (!cur) return [];
  const seen = new Set([cur.id]);
  // walk back to the head
  while (cur.properties?.linkPrevId && byId.has(cur.properties.linkPrevId) && !seen.has(cur.properties.linkPrevId)) {
    cur = byId.get(cur.properties.linkPrevId);
    seen.add(cur.id);
  }
  const chain = [cur];
  const walked = new Set([cur.id]);
  while (cur.properties?.linkNextId && byId.has(cur.properties.linkNextId) && !walked.has(cur.properties.linkNextId)) {
    cur = byId.get(cur.properties.linkNextId);
    walked.add(cur.id);
    chain.push(cur);
  }
  return chain;
};

export const countWords = (text) => (String(text || '').trim().match(/\S+/g) || []).length;

/** The text a box actually shows: what flows into it minus what flows on. */
export const boxVisibleText = (box, textFlow = {}) => {
  const p = box.properties || {};
  const incoming = p.linkPrevId ? textFlow[p.linkPrevId] ?? '' : storyPlainText(p);
  // what flows on to the next box — or, for the last box, what does not fit (CanvasObject records both)
  const outgoing = textFlow[box.id] || '';
  return outgoing ? storyPrefix(incoming, outgoing) : incoming;
};

export const snippetOf = (text, max = 44) => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}\u2026` : t;
};

/** Story totals: words in the whole story, words per box, words still in overflow after the last box. */
export const storyStats = (chain, textFlow = {}) => {
  const head = chain[0];
  const tail = chain[chain.length - 1];
  const story = head ? storyPlainText(head.properties || {}) : '';
  const overflow = tail ? textFlow[tail.id] || '' : '';
  return {
    words: countWords(story),
    overflowWords: countWords(overflow),
    boxes: chain.map((box) => {
      const visible = boxVisibleText(box, textFlow);
      return { id: box.id, words: countWords(visible), snippet: snippetOf(visible) };
    }),
  };
};
